// frontend/src/pages/manufacturing/DataImport.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AccountTree as BOMIcon,
  Inventory as ItemIcon,
  Build as OperationIcon,
  Assignment as WorkOrderIcon,
  People as CustomerIcon,
  Download as DownloadIcon,
  Layers as HierarchyIcon,
  ShoppingCart as SageIcon,
  Phone as PhoneIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';
import usePageTitle from '../../hooks/usePageTitle';

const importTypes = [
  {
    id: 'zero_out_adhesive',
    name: '0 Out Adhesive',
    icon: <BOMIcon />,
    description: 'Zero out quantity_required for BOM components based on item codes in file',
    templateUrl: null,
    requiredFields: ['Item No.'],
    optionalFields: ['Quantity', 'Warehouse Code', 'Item Description'],
    sampleData: [
      { 'Item No.': 'AT/AA60001_002', 'Quantity': '', 'Warehouse Code': '01', 'Item Description': 'Adhesive useage Square Metre' },
      { 'Item No.': 'AT/AA60002_002', 'Quantity': '', 'Warehouse Code': '01', 'Item Description': 'Adhesive useage Square Metre' },
    ],
    importInstructions: [
      'Upload Excel/CSV with item codes in "Item No." column',
      'System will find all BOMs where these items are components',
      'Sets quantity_required to 0 (does NOT deactivate the BOM entry)',
      'Items not found in database will be skipped',
      'A log of all changes will be provided'
    ]
  },
  {
    id: 'bom',
    name: 'Bill of Materials (BOM)',
    icon: <BOMIcon />,
    description: 'Import parent-child relationships for product structures',
    templateUrl: '/templates/bom_template.csv',
    requiredFields: ['ParentItem', 'ComponentItem', 'Quantity'],
    optionalFields: ['ParentItemName', 'UoM', 'Operation', 'Notes'],
    sampleData: [
      { ParentItem: 'TABLE-001', ParentItemName: 'Dining Table', ComponentItem: 'LEG-001', Quantity: '4', UoM: 'each' },
      { ParentItem: 'TABLE-001', ParentItemName: 'Dining Table', ComponentItem: 'TOP-001', Quantity: '1', UoM: 'each' },
    ]
  },
  {
    id: 'hierarchical_bom',
    name: 'Hierarchical BOM (Sage)',
    icon: <HierarchyIcon />,
    description: 'Import multi-level BOM structure with operations from Sage export',
    templateUrl: '/templates/hierarchical_bom_template.csv',
    requiredFields: ['ParentItem', 'ComponentItem', 'QtyPerParent'],
    optionalFields: ['Level', 'ComponentDesc', 'UoM', 'AG_ID', 'OperationDesc', 'TimePerUnit', 'OperationScrap'],
    sampleData: [
      { ParentItem: 'TABLE-001', ComponentItem: 'LEG-ASSY', QtyPerParent: '1', Level: '1', AG_ID: '10', OperationDesc: 'Assembly', TimePerUnit: '15' },
      { ParentItem: 'LEG-ASSY', ComponentItem: 'STEEL-ROD', QtyPerParent: '4', Level: '2', AG_ID: '20', OperationDesc: 'Cutting', TimePerUnit: '5' },
    ]
  },
  {
    id: 'table_top_boms',
    name: 'Table Tops (Items + BOM + Routing)',
    icon: <BOMIcon />,
    description: 'Wide-format BOM sheet: creates items, their BOMs, and the 3-step Cut Laminates → Enviro Tops → Inject Polyedge routing',
    templateUrl: null,
    requiredFields: ['PARENT', 'DESCRIPTION'],
    optionalFields: ['MATERIALS'],
    sampleData: [
      { PARENT: 'GR8TTGP15', DESCRIPTION: 'Go-Roll 4ft Table Top GP15 White', MATERIALS: 'XCA01015 × 0.17, XCA03096 × 0.17, GR00047 × 0.25' },
      { PARENT: 'GR10TTGP23', DESCRIPTION: 'Go-Roll 5ft Table Top GP23 Yellow', MATERIALS: 'XCA01023 × 0.25, XCA03096 × 0.25, GR00047 × 0.25' },
    ],
    importInstructions: [
      'Column 1 = item code, column 2 = description, then repeating RAW MATERIAL / QUANTITY column pairs',
      'Creates each item (manufactured, EA) if it does not exist; updates the name if it changed',
      'Creates/updates one BOM row per raw material with the quantity needed per top',
      'Applies the 3-step routing: OP-20 Cut Laminates → OP-13 Enviro Tops → OP-18 Inject Polyedge',
      'ALL raw materials are consumed when the Cut Laminates (OP-20) step is started',
      'Raw materials missing from the system are auto-created as raw-material items',
      'Re-uploading the same sheet is safe — quantities are updated and removed materials are deactivated',
    ]
  },
  {
    id: 'sage_products',
    name: 'Sage Products (create items + stock)',
    icon: <ItemIcon />,
    description: 'Sage product export: creates items with initial stock, cost prices and supplier info in one go',
    templateUrl: null,
    requiredFields: ['Product Code', 'Description'],
    optionalFields: ['Quantity In Stock', 'Last Cost Price (Std)', 'Supplier A/C', 'Part No.', 'Nominal Code', 'Bar Code', 'Inactive'],
    sampleData: [
      { 'Product Code': 'GR00001', 'Description': 'Button Clip Bespoke', 'Quantity In Stock': '2000', 'Last Cost Price (Std)': '0.87', 'Supplier A/C': 'SPING' },
      { 'Product Code': 'GR00002', 'Description': 'Torsion bar hex disc EN1A / 10MM HEX', 'Quantity In Stock': '1680', 'Last Cost Price (Std)': '1.6', 'Supplier A/C': 'TECHFORE' },
    ],
    importInstructions: [
      'Upload the Sage 50 product export (Excel/CSV) directly - e.g. the Go-Roll "Book1" sheet',
      'New items are created as raw materials; the Description becomes the item name',
      'Initial stock is set from "Quantity In Stock" via an audited stock adjustment',
      '"Last Cost Price (Std)" fills both the cost price and standard cost',
      'Supplier A/C, Part No., Nominal Code and Bar Code are kept in the item description',
      'Items that already exist only get their cost price refreshed - their stock is NEVER changed',
    ]
  },
  {
    id: 'items',
    name: 'Items / Products',
    icon: <ItemIcon />,
    description: 'Import raw materials, components, and finished products',
    templateUrl: '/templates/items_template.csv',
    requiredFields: ['ItemCode', 'Name', 'ItemType'],
    optionalFields: ['Description', 'UnitOfMeasure', 'StandardCost', 'ReorderLevel'],
    sampleData: [
      { ItemCode: 'STEEL-ROD-10', Name: 'Steel Rod 10mm', ItemType: 'raw_material', UnitOfMeasure: 'meter', StandardCost: '5.50' },
      { ItemCode: 'TABLE-LEG-STD', Name: 'Standard Table Leg', ItemType: 'manufactured', UnitOfMeasure: 'each', StandardCost: '15.00' },
    ]
  },
  {
    id: 'operations',
    name: 'Operations',
    icon: <OperationIcon />,
    description: 'Import manufacturing operations and processes',
    templateUrl: '/templates/operations_template.csv',
    requiredFields: ['OperationCode', 'Name'],
    optionalFields: ['Description', 'SetupTime', 'CycleTime', 'Instructions'],
    sampleData: [
      { OperationCode: 'CUT-01', Name: 'Metal Cutting', SetupTime: '15', CycleTime: '5' },
      { OperationCode: 'WELD-01', Name: 'MIG Welding', SetupTime: '30', CycleTime: '10' },
    ]
  },
  {
    id: 'work_orders',
    name: 'Work Orders',
    icon: <WorkOrderIcon />,
    description: 'Import production orders',
    templateUrl: '/templates/work_orders_template.csv',
    requiredFields: ['WorkOrderNumber', 'ItemCode', 'Quantity', 'DueDate'],
    optionalFields: ['Priority', 'CustomerRef', 'Notes'],
    sampleData: [
      { WorkOrderNumber: 'WO-2024-001', ItemCode: 'TABLE-001', Quantity: '10', DueDate: '2024-12-31', Priority: '2' },
    ]
  },
  {
    id: 'stock',
    name: 'Stock Levels',
    icon: <ItemIcon />,
    description: 'Import or update stock quantities - creates missing items automatically',
    templateUrl: '/templates/stock_template.csv',
    requiredFields: ['Item No.', 'In Stock'],
    optionalFields: ['Item Description', 'Stock UoM'],
    sampleData: [
      { 'Item No.': 'M0135001', 'Item Description': 'Round Insert 40mm', 'In Stock': '2361', 'Stock UoM': 'each' },
      { 'Item No.': 'M0135002', 'Item Description': 'Square Insert 40mm', 'In Stock': '1650', 'Stock UoM': 'each' },
    ],
    importInstructions: [
      'Missing items will be AUTO-CREATED as raw materials with default settings',
      'Item Description column will be used for new item names',
      'Stock quantities will be SET to the values in the CSV (not added)',
      'Negative quantities are not allowed',
    ]
  },
    {
    id: 'weights',
    name: 'Item Weights',
    icon: <ItemIcon />,
    description: 'Import or update item weights from SAP export',
    templateUrl: null,
    requiredFields: ['SAP SKU', 'Weight'],
    optionalFields: [],
    sampleData: [
      { 'SAP SKU': 'C/AF250001_000', 'Weight': '6.5' },
      { 'SAP SKU': 'C/AF250002_000', 'Weight': '6.5' },
      { 'SAP SKU': 'C/AF250003_000', 'Weight': '6.5' },
    ],
    importInstructions: [
      'Upload Excel/CSV with item codes in "SAP SKU" column',
      'Weight column should contain numeric values (in kg)',
      'Items must already exist in the system',
      'Items not found will be skipped (not created)',
      'Existing weights will be overwritten'
    ]
  },
  {
    id: 'cost_prices',
    name: 'Cost Prices',
    icon: <ItemIcon />,
    description: 'Import or update latest cost prices from Sage product export',
    templateUrl: null,
    requiredFields: ['Product Code', 'Last Cost Price (Std)'],
    optionalFields: [],
    sampleData: [
      { 'Product Code': 'A2501', 'Last Cost Price (Std)': '119.61' },
      { 'Product Code': 'A3001', 'Last Cost Price (Std)': '71.07' },
      { 'Product Code': 'A4001', 'Last Cost Price (Std)': '0' },
    ],
    importInstructions: [
      'Upload the Sage product export (Excel/CSV) with "Product Code" and "Last Cost Price (Std)" columns',
      'Extra columns (Sales Price, stock etc.) are ignored',
      'Items must already exist in the system',
      'Items not found will be skipped (not created)',
      'Existing cost prices will be overwritten'
    ]
  },
  {
    id: 'temp_stock_10000',
    name: 'Set Temp Stock (10000)',
    icon: <ItemIcon />,
    description: 'Set stock level to 10,000 for all items in the file (for testing/temporary stock)',
    templateUrl: null,
    requiredFields: ['ItemCode'],
    optionalFields: [],
    sampleData: [
      { 'ItemCode': 'M0101001' },
      { 'ItemCode': 'M0101002' },
      { 'ItemCode': 'M0101003' },
    ],
    importInstructions: [
      'Upload CSV/Excel with item codes in "ItemCode" column',
      'All matching items will have stock set to exactly 10,000',
      'Items not found in the system will be skipped (not created)',
      'Useful for testing or setting temporary stock levels',
      'WARNING: This will overwrite existing stock quantities!'
    ]
  },
  {
    id: 'bom_qty_update',
    name: 'Update BOM Quantities',
    icon: <BOMIcon />,
    description: 'Update quantity_required for existing BOM relationships from spreadsheet',
    templateUrl: null,
    requiredFields: ['component_item', 'parent_item', 'qty'],
    optionalFields: ['component_desc', 'parent_desc'],
    sampleData: [
      { component_item: 'AT/AA60025_001', component_desc: '6/30 Tub Top Assembly', qty: '1', parent_item: 'AT/AA60025_000', parent_desc: '' },
      { component_item: 'AT/AA60025_003', component_desc: '6/30 Tub Table Cut Laminate', qty: '2', parent_item: 'AT/AA60025_001', parent_desc: '6/30 Tub Top Assembly' },
    ],
    importInstructions: [
      'Upload Excel/CSV with component_item, parent_item, and qty columns',
      'Only EXISTING BOM relationships will be updated',
      'BOM relationships not found in system will be skipped (not created)',
      'Items not found in system will be skipped with warning',
      'BOMs in system but not in file will remain unchanged',
      'Quantities can be decimal values (e.g., 0.00012) or whole numbers'
    ]
  },
  {
    id: 'item_descriptions',
    name: 'Item Descriptions',
    icon: <ItemIcon />,
    description: 'Update item names/descriptions from SAP export (multiple files supported)',
    templateUrl: null,
    requiredFields: ['SAP SKU', 'Item Description'],
    optionalFields: [],
    sampleData: [
      { 'SAP SKU': 'C/AF250001_000', 'Item Description': 'Contour25 Stacking Bench (6/10) 1830 x 254mm GP38 Beech 483mm High' },
      { 'SAP SKU': 'C/AF250002_000', 'Item Description': 'Contour25 Stacking Bench (6/10) 1830 x 254mm GP38 Beech 432mm High' },
      { 'SAP SKU': 'C/AF250003_000', 'Item Description': 'Contour25 Stacking Bench (6/10) 1830 x 254mm GP23 Yellow 381mm High' },
    ],
    importInstructions: [
      'Upload one or more Excel/CSV files with item codes and descriptions',
      'Columns can be named: "SAP SKU", "item_code", "sku" for item code',
      'Columns can be named: "Item Description", "name", "description" for description',
      'Items must already exist in the system (not created automatically)',
      'Items not found will be skipped with a warning',
      'Items with unchanged names will be skipped',
      'Multiple files can be uploaded at once'
    ],
    multipleFiles: true  // Flag to indicate this supports multiple files
  },
  {
    id: 'bom_component_swap',
    name: 'BOM Component Swap (Qty/2)',
    icon: <SwapIcon />,
    description: 'Replace BOM components with new items and halve the quantity',
    templateUrl: null,
    requiredFields: ['Item No.', 'To the following qty divided by 2'],
    optionalFields: ['Item Description'],
    sampleData: [
      { 'Item No.': 'XBA01015', 'To the following qty divided by 2': 'XCA01015', 'Item Description': 'GP15 White (3103) 1830 x 1540 x 0.9mm' },
      { 'Item No.': 'XBA01023', 'To the following qty divided by 2': 'XCA01023', 'Item Description': 'GP23 Yellow (3127) 1830 x 1540 x 0.9mm' },
    ],
    importInstructions: [
      'Upload Excel/CSV with old component item in "Item No." column',
      'New replacement item in "To the following qty divided by 2" column',
      'All BOMs using the old component will be updated',
      'Component will be swapped to the new item',
      'Quantity will be divided by 2',
      'Both items must exist in the system'
    ]
  },
  {
    id: 'polyedge_replacement',
    name: 'Polyedge Replacement',
    icon: <SwapIcon />,
    description: 'Replace Polyedge Metre Run components with new items (qty set to 1)',
    templateUrl: null,
    requiredFields: ['Item No.', 'New'],
    optionalFields: ['Quantity', 'Warehouse Code', 'Item Description', 'In Stock'],
    sampleData: [
      { 'Item No.': 'P/AA30001_005', 'New': 'SAA90000', 'Item Description': 'Polyedge Metre Run' },
      { 'Item No.': 'P/AA30002_005', 'New': 'SAA90000', 'Item Description': 'Polyedge Metre Run' },
      { 'Item No.': 'P/AA30003_005', 'New': 'SAA90000', 'Item Description': 'Polyedge Metre Run' },
    ],
    importInstructions: [
      'Upload Excel/CSV with old Polyedge item codes in "Item No." column',
      'New replacement item codes go in the "New" column',
      'Only items with "Polyedge" in their name will be processed',
      'All matching BOM entries will have their component swapped',
      'Quantity will be set to 1 for all replacements',
      'Both old and new items must exist in the system'
    ]
  },
  {
    id: 'item_additional_instructions',
    name: 'Item Additional Instructions',
    icon: <ItemIcon />,
    description: 'Import additional instructions/routing descriptions for items',
    templateUrl: null,
    requiredFields: ['Item No.', 'routingdescription'],
    optionalFields: ['Quantity', 'Warehouse Code', 'Item Description', 'In Stock', 'Counted Yes/No', 'Average Price'],
    sampleData: [
      { 'Item No.': 'AL/CC70001_001', 'routingdescription': 'Fit Legs to Frames 698mm', 'Item Description': '4/24 Ali Table Frame & Leg Assembly' },
      { 'Item No.': 'AL/CC70002_001', 'routingdescription': 'Fit Legs to Frames 760mm', 'Item Description': '4/24 Ali Table Frame & Leg Assembly' },
    ],
    importInstructions: [
      'Upload Excel/CSV with item codes in "Item No." column',
      'Routing description column will be saved as additional instructions',
      'Items must already exist in the system (not created automatically)',
      'Items not found will be skipped with a warning',
      'Rows with empty routingdescription will be skipped',
      'Existing additional instructions will be overwritten'
    ]
  },
  {
    id: 'delivery_info_update',
    name: 'Update Delivery Info',
    icon: <SageIcon />,
    description: 'Update delivery address information for existing orders from Sage CSV',
    templateUrl: null,
    requiredFields: ['Sales Order', 'Delivery Address'],
    optionalFields: ['Customer', 'Order Date', 'Customer Order Number'],
    sampleData: [
      { 'Sales Order': '12345', 'Customer': 'ABC School', 'Delivery Address': 'ABC Primary School, 123 High Street, London, SW1A 1AA' },
      { 'Sales Order': '12346', 'Customer': 'XYZ Academy', 'Delivery Address': 'XYZ Academy, 45 Park Road, Manchester, M1 2AB' },
    ],
    importInstructions: [
      'Uses the same Sage CSV format as the order import',
      'Matches orders by Sales Order number',
      'Updates ALL delivery fields (name, address lines, city, county, postcode)',
      'Orders not found in system will be skipped',
      'Overwrites existing delivery info completely',
      'Import orders first using the Sage Daily Import above'
    ]
  },
];

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const DataImport = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [previewData, setPreviewData] = useState([]);

  // Sage daily import state
  const [sageFile, setSageFile] = useState(null);
  const [sageImporting, setSageImporting] = useState(false);
  const [sageResults, setSageResults] = useState(null);
  const [sageError, setSageError] = useState(null);

  const steps = ['Select Type & Upload', 'Validate', 'Preview', 'Import'];

  usePageTitle('Data Import');

  const handleSageDailyImport = async () => {
    if (!sageFile) {
      toast.error('Please select a Sage CSV file');
      return;
    }

    setSageImporting(true);
    setSageError(null);
    setSageResults(null);

    const formData = new FormData();
    formData.append('file', sageFile);

    try {
      const response = await manufacturingAPI.customerOrders.importSageOrders(formData);
      
      const summary = response.data.summary;
      setSageResults(summary);
      
      // Build detailed success message
      const messageParts = [];
      if (summary.customers_created > 0) messageParts.push(`${summary.customers_created} customers`);
      if (summary.orders_created > 0) messageParts.push(`${summary.orders_created} orders`);
      if (summary.lines_created > 0) messageParts.push(`${summary.lines_created} lines`);
      if (summary.special_codes_created > 0) messageParts.push(`${summary.special_codes_created} special codes`);
      
      toast.success(
        <div>
          <strong>Sage Import Complete!</strong>
          <div style={{marginTop: '8px'}}>
            {messageParts.length > 0 ? `Created: ${messageParts.join(', ')}` : 'No changes made'}
          </div>
        </div>,
        { autoClose: 8000 }
      );
      
      // Show warnings if any
      if (summary.unrecognized_codes && summary.unrecognized_codes.length > 0) {
        toast.warning(
          `Unrecognized codes: ${summary.unrecognized_codes.join(', ')}. Add them as Special Code Types if needed.`,
          { autoClose: 10000 }
        );
      }
      
    } catch (error) {
      console.error('Sage import error:', error);
      
      // Check if it's a validation error (400) with missing items
      if (error.response?.status === 400 && error.response?.data?.summary) {
        // This is our validation error
        setSageError({
          type: 'validation',
          message: error.response.data.error || error.response.data.message,
          summary: error.response.data.summary
        });
        
        toast.error(
          <div>
            <strong>Import Validation Failed</strong>
            <div style={{marginTop: '8px'}}>
              {error.response.data.error}
            </div>
          </div>,
          { autoClose: 10000 }
        );
      } else {
        // Other errors
        setSageError({
          type: 'error',
          message: error.response?.data?.error || error.message || 'Import failed'
        });
        
        toast.error(error.response?.data?.error || 'Import failed');
      }
      
      setSageResults(null);
    } finally {
      setSageImporting(false);
    }
  };

  const resetSageImport = () => {
    setSageFile(null);
    setSageResults(null);
    setSageError(null);
  };

  const handleFileUpload = async (event) => {
    const uploadedFiles = event.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    const currentImportType = importTypes[selectedType];
    
    // Check file types
    for (let i = 0; i < uploadedFiles.length; i++) {
      const fileType = uploadedFiles[i].name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(fileType)) {
        toast.error(`Invalid file type: ${uploadedFiles[i].name}. Please upload CSV or Excel files.`);
        return;
      }
    }

    // For multiple file imports
    if (currentImportType.multipleFiles) {
      setFile(uploadedFiles);  // Store FileList
      
      const formData = new FormData();
      for (let i = 0; i < uploadedFiles.length; i++) {
        formData.append('files', uploadedFiles[i]);
      }
      formData.append('import_type', currentImportType.id);
      
      try {
        let response;
        if (currentImportType.id === 'item_descriptions') {
          response = await manufacturingAPI.items.validateDescriptionImport(formData);
        } else if (currentImportType.id === 'bom_component_swap') {
          response = await manufacturingAPI.bom.validateComponentSwap(formData);
        } else {
          response = await manufacturingAPI.validateImport(formData);
        }
        
        if (response.data.success) {
          setValidationResults(response.data.validation);
          setPreviewData(response.data.validation?.preview || []);
          setActiveStep(1);
          toast.success(`${uploadedFiles.length} file(s) validated successfully`);
        }
      } catch (error) {
        toast.error('File validation failed: ' + (error.response?.data?.error || error.message));
      }
    } else {
      // Single file import (existing logic)
      const uploadedFile = uploadedFiles[0];
      setFile(uploadedFile);
      
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('import_type', currentImportType.id);
      
      try {
        let response;
        if (currentImportType.id === 'bom_component_swap') {
          response = await manufacturingAPI.bom.validateComponentSwap(formData);
        } else {
          response = await manufacturingAPI.validateImport(formData);
        }
        
        if (response.data.success) {
          setValidationResults(response.data.validation);
          setPreviewData(response.data.preview || []);
          setActiveStep(1);
          toast.success('File validated successfully');
        }
      } catch (error) {
        toast.error('File validation failed: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleImport = async () => {
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('import_type', importTypes[selectedType].id);
    
    try {
      let response;
      const importType = importTypes[selectedType].id;
      
      // Call the appropriate import endpoint based on type
      switch (importType) {
        case 'bom':
          response = await manufacturingAPI.bom.importBOM(formData);
          break;
        case 'hierarchical_bom':
          response = await manufacturingAPI.bom.importHierarchicalBOM(formData);
          break;
        case 'items':
          response = await manufacturingAPI.items.importItems(formData);
          break;
        case 'sage_products':
          response = await manufacturingAPI.items.importSageProducts(formData);
          break;
        case 'table_top_boms':
          response = await manufacturingAPI.items.importTableTopBoms(formData);
          break;
        case 'operations':
          response = await manufacturingAPI.operations.importOperations(formData);
          break;
        case 'work_orders':
          response = await manufacturingAPI.workOrders.importWorkOrders(formData);
          break;
        case 'stock':
          response = await manufacturingAPI.stockLevels.importStock(formData);
          break;
        case 'zero_out_adhesive':
          response = await manufacturingAPI.bom.zeroOutAdhesive(formData);
          break;
        case 'weights':
          response = await manufacturingAPI.items.importWeights(formData);
          break;
        case 'cost_prices':
          response = await manufacturingAPI.items.importCostPrices(formData);
          break;
        case 'temp_stock_10000':
          response = await manufacturingAPI.stockLevels.setTempStock10000(formData);
          break;
        case 'bom_qty_update':
          response = await manufacturingAPI.bom.updateBOMQuantities(formData);
          break;

        case 'item_descriptions':
          // Multiple files
          const descFormData = new FormData();
          if (file instanceof FileList) {
            for (let i = 0; i < file.length; i++) {
              descFormData.append('files', file[i]);
            }
          } else {
            descFormData.append('files', file);
          }
          response = await manufacturingAPI.items.importDescriptions(descFormData);
          break;

        case 'bom_component_swap':
          response = await manufacturingAPI.bom.componentSwap(formData);
          break;

        case 'polyedge_replacement':
          response = await manufacturingAPI.bom.polyedgeReplacement(formData);
          break;

        case 'item_additional_instructions':
          response = await manufacturingAPI.items.importAdditionalInstructions(formData);
          break;

        case 'delivery_info_update':
          response = await manufacturingAPI.customerOrders.updateDeliveryInfo(formData);
          break;

        default:
          throw new Error('Unknown import type');
      }
      
      if (response.data.success) {
        setImportResults(response.data);
        setActiveStep(3);
        toast.success('Import completed successfully!');
      }
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.error || error.message));
      setImporting(false);
    }
  };

  const resetImport = () => {
    setActiveStep(0);
    setFile(null);
    setFileData(null);
    setValidationResults(null);
    setImportResults(null);
    setPreviewData([]);
    setImporting(false);
  };

  const downloadTemplate = (importType) => {
    const headers = [...importType.requiredFields, ...importType.optionalFields].join(',');
    const sampleRows = importType.sampleData.map(row => 
      [...importType.requiredFields, ...importType.optionalFields].map(field => row[field] || '').join(',')
    ).join('\n');
    
    const csvContent = `${headers}\n${sampleRows}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${importType.id}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* SAGE DAILY IMPORT - TOP SECTION */}
      <Paper sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa', borderLeft: '4px solid #1976d2' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SageIcon sx={{ fontSize: 40, mr: 2, color: '#1976d2' }} />
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              Daily Sage 50 Order Import
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quick import for today's customer orders from Sage
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Quick Import Instructions:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText 
                    primary="Export sales orders from Sage 50 as CSV"
                    secondary="File → Export → Sales Orders"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PhoneIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="BOOKIN codes will be extracted as contact phone numbers"
                    secondary="Multiple BOOKIN entries per order are supported"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText 
                    primary="Products must exist in system first"
                    secondary="Import will fail if any products are missing"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText 
                    primary="Orders can be re-imported to update"
                    secondary="Existing orders will be updated, not duplicated"
                  />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="sage-daily-upload"
                type="file"
                onChange={(e) => {
                  setSageFile(e.target.files[0]);
                  setSageResults(null);
                  setSageError(null);
                }}
              />
              <label htmlFor="sage-daily-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={sageImporting}
                >
                  {sageFile ? sageFile.name : 'Choose Sage CSV'}
                </Button>
              </label>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSageDailyImport}
                disabled={!sageFile || sageImporting}
                startIcon={sageImporting ? <CircularProgress size={20} /> : <SageIcon />}
              >
                {sageImporting ? 'Importing...' : 'Import Orders'}
              </Button>

              {sageFile && !sageImporting && (
                <Button
                  variant="text"
                  onClick={resetSageImport}
                  disabled={sageImporting}
                >
                  Clear
                </Button>
              )}
            </Box>

            {sageFile && !sageResults && !sageError && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Selected file:</strong> {sageFile.name} ({(sageFile.size / 1024).toFixed(1)} KB)
                </Typography>
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'white' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Quick Links
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText>
                      <Link href="/settings" onClick={(e) => { e.preventDefault(); navigate('/settings'); }} underline="hover">
                        Manage Special Code Types →
                      </Link>
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      <Link 
                        component="button" 
                        onClick={() => navigate('/manufacturing/customer-orders')}
                        underline="hover"
                      >
                        View Customer Orders →
                      </Link>
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      <Link 
                        component="button" 
                        onClick={() => navigate('/manufacturing/items')}
                        underline="hover"
                      >
                        Manage Items →
                      </Link>
                    </ListItemText>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Sage Validation Error Display */}
        {sageError && sageError.type === 'validation' && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                ❌ Import Validation Failed
              </Typography>
              <Typography variant="body1" paragraph>
                {sageError.message}
              </Typography>
              <Typography variant="body2" color="error.dark" sx={{ fontWeight: 'bold' }}>
                ⚠️ NO DATA WAS IMPORTED - All items must exist before importing orders
              </Typography>
            </Alert>

            {sageError.summary?.missing_items && sageError.summary.missing_items.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Missing Items ({sageError.summary.missing_items.length})
                </Typography>
                <Typography variant="body2" paragraph>
                  The following product codes do not exist in your system. Create them first, then re-import:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {sageError.summary.missing_items.slice(0, 20).map(item => (
                    <Chip 
                      key={item} 
                      label={item} 
                      size="small" 
                      color="error"
                      icon={<ErrorIcon />}
                    />
                  ))}
                  {sageError.summary.missing_items.length > 20 && (
                    <Chip 
                      label={`+${sageError.summary.missing_items.length - 20} more`} 
                      size="small" 
                      color="error"
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => {
                      // Copy missing items to clipboard
                      const itemsText = sageError.summary.missing_items.join('\n');
                      navigator.clipboard.writeText(itemsText);
                      toast.success('Missing items copied to clipboard');
                    }}
                  >
                    Copy Missing Items
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/manufacturing/items')}
                  >
                    Go to Items Management →
                  </Button>
                </Box>
              </Alert>
            )}

            {sageError.summary?.unrecognized_codes && sageError.summary.unrecognized_codes.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Unrecognized Special Codes
                </Typography>
                <Typography variant="body2" paragraph>
                  These codes might be special codes (like BOOKIN). If so, add them in Settings:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {sageError.summary.unrecognized_codes.map(code => (
                    <Chip key={code} label={code} size="small" color="warning" />
                  ))}
                </Box>
                <Link 
                  component="button"
                  onClick={() => navigate('/settings')}
                  underline="hover"
                >
                  Add Special Code Types →
                </Link>
              </Alert>
            )}

            {sageError.summary?.errors && sageError.summary.errors.length > 0 && (
              <Alert severity="error">
                <Typography variant="subtitle2" gutterBottom>
                  Additional Errors ({sageError.summary.errors.length})
                </Typography>
                <List dense>
                  {sageError.summary.errors.slice(0, 5).map((error, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon><ErrorIcon fontSize="small" color="error" /></ListItemIcon>
                      <ListItemText 
                        primary={error}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
                {sageError.summary.errors.length > 5 && (
                  <Typography variant="body2">
                    ...and {sageError.summary.errors.length - 5} more errors
                  </Typography>
                )}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={resetSageImport}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        )}

        {/* General Error Display */}
        {sageError && sageError.type === 'error' && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Import Error
            </Typography>
            <Typography variant="body2">
              {sageError.message}
            </Typography>
            <Button
              variant="text"
              size="small"
              sx={{ mt: 1 }}
              onClick={resetSageImport}
            >
              Try Again
            </Button>
          </Alert>
        )}

        {/* Sage Import Success Results */}
        {sageResults && !sageError && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                ✓ Import Complete!
              </Typography>
              <Typography variant="body2">
                {sageResults.message || 'Orders imported successfully'}
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Customers
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {sageResults.customers_created || 0}
                    </Typography>
                    <Typography variant="caption">created</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Orders
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {sageResults.orders_created || 0}
                    </Typography>
                    <Typography variant="caption">created</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Order Lines
                    </Typography>
                    <Typography variant="h6" color="info.main">
                      {sageResults.lines_created || 0}
                    </Typography>
                    <Typography variant="caption">created</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">
                      Special Codes
                    </Typography>
                    <Typography variant="h6" color="secondary.main">
                      {sageResults.special_codes_created || 0}
                    </Typography>
                    <Typography variant="caption">BOOKIN etc</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {sageResults.unrecognized_codes && sageResults.unrecognized_codes.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Unrecognized Special Codes
                </Typography>
                <Typography variant="body2" paragraph>
                  These codes were treated as regular products. If they're special codes (like BOOKIN), add them:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {sageResults.unrecognized_codes.map(code => (
                    <Chip key={code} label={code} size="small" color="info" />
                  ))}
                </Box>
                <Link 
                  component="button"
                  onClick={() => navigate('/settings')}
                  underline="hover"
                >
                  Add Special Code Types →
                </Link>
              </Alert>
            )}

            {sageResults.errors && sageResults.errors.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Warnings ({sageResults.errors.length})
                </Typography>
                <List dense>
                  {sageResults.errors.slice(0, 5).map((error, idx) => (
                    <ListItem key={idx}>
                      <ListItemText 
                        primary={error}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
                {sageResults.errors.length > 5 && (
                  <Typography variant="body2">
                    ...and {sageResults.errors.length - 5} more
                  </Typography>
                )}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/manufacturing/customer-orders')}
              >
                View Imported Orders
              </Button>
              <Button
                variant="outlined"
                onClick={resetSageImport}
              >
                Import More
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* EXISTING BULK IMPORT SECTION */}
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Bulk Data Import
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Import various types of data into your manufacturing system
        </Typography>

        <Stepper activeStep={activeStep} sx={{ my: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Select Import Type */}
        {activeStep === 0 && (
          <Box>
            <Tabs value={selectedType} onChange={(e, newValue) => setSelectedType(newValue)} variant="scrollable" scrollButtons="auto">
              {importTypes.map((type, index) => (
                <Tab 
                  key={type.id} 
                  label={type.name} 
                  icon={type.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>

            {importTypes.map((type, index) => (
              <TabPanel key={type.id} value={selectedType} index={index}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {type.name} Import
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {type.description}
                        </Typography>
                        
                        {type.importInstructions && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Instructions:
                            </Typography>
                            <List dense>
                              {type.importInstructions.map((instruction, idx) => (
                                <ListItem key={idx}>
                                  <ListItemIcon><InfoIcon fontSize="small" color="primary" /></ListItemIcon>
                                  <ListItemText primary={instruction} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Required Fields:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {type.requiredFields.map(field => (
                              <Chip key={field} label={field} size="small" color="primary" />
                            ))}
                          </Box>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Optional Fields:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {type.optionalFields.map(field => (
                              <Chip key={field} label={field} size="small" />
                            ))}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <input
                            accept=".csv,.xlsx,.xls"
                            style={{ display: 'none' }}
                            id={`file-upload-${type.id}`}
                            type="file"
                            multiple={type.multipleFiles || false}
                            onChange={handleFileUpload}
                          />
                          <label htmlFor={`file-upload-${type.id}`}>
                            <Button
                              variant="contained"
                              component="span"
                              startIcon={<UploadIcon />}
                            >
                              Upload File
                            </Button>
                          </label>
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => downloadTemplate(type)}
                          >
                            Download Template
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Import Guidelines
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                            <ListItemText 
                              primary="File Format" 
                              secondary="CSV (recommended) or Excel"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                            <ListItemText 
                              primary="First Row" 
                              secondary="Must contain column headers"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                            <ListItemText 
                              primary="Encoding" 
                              secondary="UTF-8 or Latin-1"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
                            <ListItemText 
                              primary="Max Size" 
                              secondary="10MB / 10,000 rows"
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>
            ))}
          </Box>
        )}

        {/* Step 1: Validation Results */}
        {activeStep === 1 && validationResults && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Validation Results
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Rows
                    </Typography>
                    <Typography variant="h4">
                      {validationResults.total_rows}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Valid Rows
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {validationResults.valid_rows}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Warnings
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {validationResults.warnings?.length || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Errors
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {validationResults.errors?.length || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Item Description specific validation info */}
            {importTypes[selectedType].id === 'item_descriptions' && validationResults && (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Items to Update
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {validationResults.items_to_update || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Not Found
                        </Typography>
                        <Typography variant="h4" color="warning.main">
                          {validationResults.items_not_found || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Unchanged
                        </Typography>
                        <Typography variant="h4" color="text.secondary">
                          {validationResults.items_unchanged || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Files
                        </Typography>
                        <Typography variant="h4">
                          {validationResults.files_processed || 0}/{validationResults.total_files || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {validationResults.not_found_codes?.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Items Not Found ({validationResults.not_found_codes.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      {validationResults.not_found_codes.slice(0, 20).map(code => (
                        <Chip key={code} label={code} size="small" color="warning" />
                      ))}
                      {validationResults.not_found_codes.length > 20 && (
                        <Chip label={`+${validationResults.not_found_codes.length - 20} more`} size="small" />
                      )}
                    </Box>
                  </Alert>
                )}

                {validationResults.preview?.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Preview of Changes (first {Math.min(validationResults.preview.length, 10)}):
                    </Typography>
                    <TableContainer sx={{ maxHeight: 200 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item Code</TableCell>
                            <TableCell>Current Name</TableCell>
                            <TableCell>New Name</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {validationResults.preview.slice(0, 10).map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{item.item_code}</TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.current_name || '(empty)'}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.new_name}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Alert>
                )}
              </Box>
            )}

            {validationResults.errors?.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Errors found:</Typography>
                <List dense>
                  {validationResults.errors.slice(0, 5).map((error, idx) => (
                    <ListItem key={idx}>
                      Row {error.row}: {error.message}
                    </ListItem>
                  ))}
                </List>
                {validationResults.errors.length > 5 && (
                  <Typography variant="body2">
                    ...and {validationResults.errors.length - 5} more errors
                  </Typography>
                )}
              </Alert>
            )}

            {validationResults.warnings?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Warnings:</Typography>
                <List dense>
                  {validationResults.warnings.slice(0, 5).map((warning, idx) => (
                    <ListItem key={idx}>
                      Row {warning.row}: {warning.message}
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={resetImport}>
                Start Over
              </Button>
              <Button 
                variant="contained" 
                onClick={() => setActiveStep(2)}
                disabled={validationResults.errors?.length > 0}
              >
                Preview Data
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Preview */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Data Preview (First 10 rows)
            </Typography>
            
            <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 3 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {importTypes[selectedType].requiredFields.concat(importTypes[selectedType].optionalFields).map(field => (
                      <TableCell key={field}>{field}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(0, 10).map((row, idx) => (
                    <TableRow key={idx}>
                      {importTypes[selectedType].requiredFields.concat(importTypes[selectedType].optionalFields).map(field => (
                        <TableCell key={field}>{row[field] || '-'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setActiveStep(1)}>
                Back
              </Button>
              <Button 
                variant="contained" 
                onClick={handleImport}
                disabled={importing}
                startIcon={importing ? <CircularProgress size={20} /> : null}
              >
                {importing ? 'Importing...' : 'Start Import'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Import Results */}
        {activeStep === 3 && importResults && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6">Import Completed Successfully!</Typography>
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Import Summary
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText 
                          primary="Total Processed"
                          secondary={importResults.summary?.processed || 0}
                        />
                      </ListItem>
                      
                      {importResults.summary?.bom_created !== undefined && (
                        <>
                          <ListItem>
                            <ListItemText 
                              primary="BOMs Created"
                              secondary={importResults.summary.bom_created}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="BOMs Updated"
                              secondary={importResults.summary.bom_updated || 0}
                            />
                          </ListItem>
                        </>
                      )}
                      
                      {importResults.summary?.created !== undefined && (
                        <ListItem>
                          <ListItemText 
                            primary="Records Created"
                            secondary={importResults.summary.created}
                          />
                        </ListItem>
                      )}
                      
                      {importResults.summary?.updated !== undefined && (
                        <ListItem>
                          <ListItemText 
                            primary="Records Updated"
                            secondary={importResults.summary.updated}
                          />
                        </ListItem>
                      )}
                      
                      {importResults.summary?.operations_created > 0 && (
                        <ListItem>
                          <ListItemText 
                            primary="Operations Auto-Created"
                            secondary={importResults.summary.operations_created}
                          />
                        </ListItem>
                      )}
                      
                      <ListItem>
                        <ListItemText 
                          primary="Skipped"
                          secondary={importResults.summary?.skipped || 0}
                        />
                      </ListItem>
                      
                      {importResults.summary?.errors?.length > 0 && (
                        <ListItem>
                          <ListItemText 
                            primary="Errors"
                            secondary={importResults.summary.errors.length}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Show errors if any */}
              {importResults.summary?.errors?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="error">
                        Import Errors
                      </Typography>
                      <List dense>
                        {importResults.summary.errors.slice(0, 10).map((error, idx) => (
                          <ListItem key={idx}>
                            <ListItemText 
                              primary={error}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                      {importResults.summary.errors.length > 10 && (
                        <Typography variant="body2" color="text.secondary">
                          ...and {importResults.summary.errors.length - 10} more errors
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={resetImport}>
                Import More Data
              </Button>
              <Button 
                variant="contained" 
                onClick={() => navigate('/manufacturing')}
              >
                Go to Manufacturing
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default DataImport;